import React from 'react';
import moment from 'moment';

const PlanChangePreviewModal = ({
    isOpen,
    isLoading,
    preview,
    onConfirm,
    onClose,
    confirmLoading,
}) => {
    if (!isOpen) return null;

    const formatMessageDate = (message) => {
        if (!message) return '';

        return message.replace(
            /\b\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z\b/g,
            (match) => moment(match).format('DD MMM YYYY')
        );
    };

    const currency = preview?.currency || 'GBP';
    const currentPlan = preview?.currentPlan || {};
    const newPlan = preview?.newPlan || {};
    const changeType = preview?.changeType || 'plan change';
    const effectiveImmediately = Boolean(preview?.effectiveImmediately);
    const periodEnd = preview?.periodEnd
        ? moment(preview.periodEnd).format('DD MMM YYYY')
        : 'N/A';
    const amountDue = preview?.proratedAmountDue ?? 0;
    const detailLabel =
        changeType === 'upgrade'
            ? 'Upgrade details'
            : 'Downgrade details';
    const billingText = effectiveImmediately
        ? 'Effective immediately'
        : `Effective at period end (${periodEnd})`;
    const previewMessage = formatMessageDate(preview?.message);

    return (
        <div className="modal fade show d-block plan-change-preview-modal" tabIndex="-1" role="dialog">
            <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
                <div className="modal-content plan-change-preview-modal__content">
                    <div className="modal-header border-0 plan-change-preview-modal__header">
                        <div className="plan-change-preview-modal__header-copy">
                            <div className="plan-change-preview-modal__eyebrow">
                                Plan preview
                            </div>
                            <h5 className="modal-title m-0 plan-change-preview-modal__title">
                                {changeType === 'upgrade' ? 'Upgrade preview' : 'Downgrade preview'}
                            </h5>
                            <p className="plan-change-preview-modal__subtitle m-0">
                                Review the change before confirming it.
                            </p>
                        </div>
                        <button
                            type="button"
                            className="close plan-change-preview-modal__close"
                            onClick={onClose}
                        >
                            &times;
                        </button>
                    </div>

                    <div className="modal-body plan-change-preview-modal__body">
                        {isLoading ? (
                            <div className="plan-change-preview-modal__loading">
                                <div className="spinner-border text-success" role="status" />
                                <div className="text-gray mt-3">Fetching plan preview...</div>
                            </div>
                        ) : preview ? (
                            <>
                                <div className="plan-change-preview-modal__summary">
                                    <div className="plan-change-preview-modal__summary-chip">
                                        {currentPlan?.name || 'Current plan'}
                                    </div>
                                    <div className="plan-change-preview-modal__summary-arrow">→</div>
                                    <div className="plan-change-preview-modal__summary-chip plan-change-preview-modal__summary-chip--accent">
                                        {newPlan?.name || 'New plan'}
                                    </div>
                                </div>

                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <div className="plan-change-preview-modal__panel">
                                            <span className="plan-change-preview-modal__label">Current plan</span>
                                            <div className="plan-change-preview-modal__value">{currentPlan?.name || 'N/A'}</div>
                                            <div className="plan-change-preview-modal__subvalue">
                                                {currency} {currentPlan?.price ?? '0'}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="plan-change-preview-modal__panel plan-change-preview-modal__panel--accent">
                                            <span className="plan-change-preview-modal__label">New plan</span>
                                            <div className="plan-change-preview-modal__value">{newPlan?.name || 'N/A'}</div>
                                            <div className="plan-change-preview-modal__subvalue">
                                                {currency} {newPlan?.price ?? '0'}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="plan-change-preview-modal__details">
                                    <div className="plan-change-preview-modal__details-heading">
                                        {detailLabel}
                                    </div>
                                    <div className="plan-change-preview-modal__detail-row">
                                        <span className="plan-change-preview-modal__detail-label">Type</span>
                                        <span className={`plan-change-preview-modal__badge plan-change-preview-modal__badge--${changeType}`}>
                                            {changeType}
                                        </span>
                                    </div>
                                    <div className="plan-change-preview-modal__detail-row">
                                        <span className="plan-change-preview-modal__detail-label">Prorated amount due</span>
                                        <span className="plan-change-preview-modal__detail-value">
                                            {currency} {Number(amountDue).toFixed(2)}
                                        </span>
                                    </div>
                                    <div className="plan-change-preview-modal__detail-row">
                                        <span className="plan-change-preview-modal__detail-label">Effective</span>
                                        <span className="plan-change-preview-modal__detail-value">
                                            {billingText}
                                        </span>
                                    </div>
                                    <div className="plan-change-preview-modal__message">
                                        {previewMessage}
                                    </div>
                                </div>
                            </>
                        ) : null}
                    </div>

                    <div className="modal-footer border-0 plan-change-preview-modal__footer">
                        <button className="btn btn-get-it-now-outline plan-change-preview-modal__secondary" onClick={onClose}>
                            Cancel
                        </button>
                        <button
                            className="btn btn-get-it-now plan-change-preview-modal__primary"
                            onClick={onConfirm}
                            disabled={confirmLoading || isLoading || !preview}
                        >
                            {confirmLoading ? 'Confirming...' : 'Confirm'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PlanChangePreviewModal;
